import React, { useEffect, useRef, useState } from 'react';
import { useLocationContext } from '../context/LocationContext';
import { getCurrentReviewerLabel } from '../api/authApi';
import {
    serviceMappingApi,
    type ServiceMappingSuggestion,
    type GHLServiceItem,
    type GHLCalendarItem,
} from '../api/serviceMappingApi';
import { SegmentedToggle } from '../components/mapping/SegmentedToggle';
import { ConfidenceBar } from '../components/mapping/ConfidenceBar';
import { StatusBadge, ReviewedByCaption } from '../components/mapping/StatusBadge';
import { ReasoningModal } from '../components/mapping/ReasoningModal';
import { ManualPicker, type PickerItem } from '../components/mapping/ManualPicker';
import { EditMappingModal } from '../components/mapping/EditMappingModal';
import { Pagination } from '../components/mapping/Pagination';
import { Dropdown } from '../components/mapping/Dropdown';
import { CONFIDENCE_TIER_OPTIONS, confidenceRangeForTier } from '../components/mapping/confidenceTier';
import { Toast } from '../components/shared/Toast';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

const PAGE_SIZE = 20;

type Mode = 'ai' | 'manual';

export const ServiceMappingPage: React.FC<{ initialStatusFilter?: string }> = ({ initialStatusFilter }) => {
    const { locationId } = useLocationContext();
    const [mode, setMode] = useState<Mode>('ai');
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const [suggestions, setSuggestions] = useState<ServiceMappingSuggestion[]>([]);
    const [total, setTotal] = useState(0);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [showConf, setShowConf] = useState(true);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState(initialStatusFilter ?? '');
    const [confidenceTier, setConfidenceTier] = useState('');
    const [serviceNameFilter, setServiceNameFilter] = useState('');
    const [calendarNameFilter, setCalendarNameFilter] = useState('');
    const [filterOptions, setFilterOptions] = useState<{ service_name: string[]; calendar_name: string[] }>({ service_name: [], calendar_name: [] });
    const [reasoningModal, setReasoningModal] = useState<{ src: string; tgt: string; reasoning: string } | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [isPolling, setIsPolling] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);
    const pollRef = useRef<{ lastTotal: number; stableCount: number } | null>(null);
    const [triggering, setTriggering] = useState(false);
    const [editSuggestion, setEditSuggestion] = useState<ServiceMappingSuggestion | null>(null);
    const [editCalendars, setEditCalendars] = useState<GHLCalendarItem[]>([]);
    const [loadingEditCalendars, setLoadingEditCalendars] = useState(false);

    const [services, setServices] = useState<GHLServiceItem[]>([]);
    const [calendars, setCalendars] = useState<GHLCalendarItem[]>([]);
    const [loadingManualData, setLoadingManualData] = useState(false);
    const [history, setHistory] = useState<{ src: string; tgt: string }[]>([]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 400);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        setPage(1);
    }, [debouncedQuery, statusFilter, confidenceTier, serviceNameFilter, calendarNameFilter]);

    useEffect(() => {
        if (!locationId) { setSuggestions([]); setTotal(0); return; }
        setLoadingSuggestions(true);
        const confRange = confidenceRangeForTier(confidenceTier);
        serviceMappingApi.getSuggestions(locationId, {
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
            search: debouncedQuery || undefined,
            status: statusFilter || undefined,
            minConfidence: confRange.min,
            maxConfidence: confRange.max,
            serviceName: serviceNameFilter || undefined,
            calendarName: calendarNameFilter || undefined,
        })
            .then(res => { setSuggestions(res.suggestions); setTotal(res.total); })
            .catch(() => { setSuggestions([]); setTotal(0); })
            .finally(() => setLoadingSuggestions(false));
    }, [locationId, refreshTrigger, page, debouncedQuery, statusFilter, confidenceTier, serviceNameFilter, calendarNameFilter]);

    useEffect(() => {
        if (!locationId) { setFilterOptions({ service_name: [], calendar_name: [] }); return; }
        serviceMappingApi.getFilterOptions(locationId)
            .then(setFilterOptions)
            .catch(() => setFilterOptions({ service_name: [], calendar_name: [] }));
    }, [locationId, refreshTrigger]);

    useEffect(() => {
        if (!isPolling || !locationId) return;
        if (!pollRef.current) pollRef.current = { lastTotal: -1, stableCount: 0 };

        const interval = setInterval(async () => {
            try {
                const res = await serviceMappingApi.getSuggestions(locationId, { limit: 1, offset: 0 });
                setRefreshTrigger(t => t + 1);
                const state = pollRef.current!;
                if (res.total === state.lastTotal) {
                    state.stableCount++;
                    if (state.stableCount >= 3) setIsPolling(false);
                } else {
                    state.lastTotal = res.total;
                    state.stableCount = 0;
                }
            } catch { /* ignore poll errors */ }
        }, 5000);

        const maxTimeout = setTimeout(() => setIsPolling(false), 180000);
        return () => { clearInterval(interval); clearTimeout(maxTimeout); };
    }, [isPolling, locationId]);

    useEffect(() => {
        if (mode !== 'manual' || !locationId) return;
        setLoadingManualData(true);
        Promise.all([
            serviceMappingApi.getGHLServices(locationId),
            serviceMappingApi.getGHLCalendars(locationId),
        ]).then(([svc, cal]) => {
            setServices(svc);
            setCalendars(cal);
        }).catch(() => setToast({ message: 'Failed to load manual mapping data', type: 'error' }))
            .finally(() => setLoadingManualData(false));
    }, [mode, locationId]);

    const handleClearFilters = () => {
        setQuery(''); setStatusFilter(''); setConfidenceTier('');
        setServiceNameFilter(''); setCalendarNameFilter('');
    };

    // Optimistic reviewer fields — an instant best-guess shown until the next
    // refetch confirms the server's exact display name (it prefers the
    // reviewer's `name` over their username; the JWT only carries the latter).
    const reviewedFields = (status: 'approved' | 'rejected') => ({
        status,
        is_reviewed: true,
        reviewed_by_name: getCurrentReviewerLabel(),
        reviewed_at: new Date().toISOString(),
    });

    const handleApprove = async (id: number) => {
        await serviceMappingApi.reviewSuggestion(id, 'approve');
        setSuggestions(prev => prev.map(s => s.id === id ? { ...s, ...reviewedFields('approved') } : s));
    };

    const handleReject = async (id: number) => {
        await serviceMappingApi.reviewSuggestion(id, 'reject');
        setSuggestions(prev => prev.map(s => s.id === id ? { ...s, ...reviewedFields('rejected') } : s));
    };

    const handleDelete = async (id: number) => {
        setDeleting(true);
        try {
            await serviceMappingApi.deleteSuggestion(id);
            setSuggestions(prev => prev.filter(s => s.id !== id));
            setTotal(t => Math.max(0, t - 1));
            setToast({ message: 'Mapping deleted', type: 'success' });
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Failed to delete mapping', type: 'error' });
        } finally {
            setDeleting(false);
            setConfirmDeleteId(null);
        }
    };

    const [confirmApproveAll, setConfirmApproveAll] = useState(false);
    const highConfCount = suggestions.filter(s => s.status === 'pending' && s.confidence_score >= 90).length;

    const requestApproveAll = () => {
        if (highConfCount === 0) return;
        setConfirmApproveAll(true);
    };

    const handleApproveAll = async () => {
        const targets = suggestions.filter(s => s.status === 'pending' && s.confidence_score >= 90);
        if (targets.length === 0) return;
        await Promise.all(targets.map(s => serviceMappingApi.reviewSuggestion(s.id, 'approve')));
        setSuggestions(prev => prev.map(s => targets.some(t => t.id === s.id) ? { ...s, ...reviewedFields('approved') } : s));
        setToast({ message: `Approved ${targets.length} high-confidence mapping(s)`, type: 'success' });
    };

    const handleRunAi = async () => {
        if (!locationId) return;
        setTriggering(true);
        try {
            await serviceMappingApi.triggerMapping(locationId);
            setRefreshTrigger(t => t + 1);
            pollRef.current = { lastTotal: -1, stableCount: 0 };
            setIsPolling(true);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to trigger mapping';
            setToast({ message: message.startsWith('EMR_NOT_SYNCED:') ? message.replace('EMR_NOT_SYNCED: ', '') : message, type: 'error' });
        } finally {
            setTriggering(false);
        }
    };

    const openEdit = async (s: ServiceMappingSuggestion) => {
        if (!locationId) return;
        setEditSuggestion(s);
        setLoadingEditCalendars(true);
        try {
            setEditCalendars(await serviceMappingApi.getGHLCalendars(locationId));
        } catch {
            setToast({ message: 'Failed to load calendars', type: 'error' });
        } finally {
            setLoadingEditCalendars(false);
        }
    };

    // No update-in-place endpoint exists for service mappings — editing re-creates the manual mapping,
    // matching the existing (already-shipped) table's behavior.
    const handleEditMap = async (ghlItem: { id: string | number; name: string }) => {
        if (!editSuggestion || !locationId) return;
        const calendar = editCalendars.find(c => c.external_id === ghlItem.id);
        if (!calendar) throw new Error('Selected calendar not found');
        await serviceMappingApi.createManualMapping({
            gh_id: locationId,
            service_external_id: editSuggestion.service_external_id,
            service_name: editSuggestion.service_name,
            calendar_external_id: calendar.external_id,
            calendar_name: calendar.name,
        });
        setRefreshTrigger(t => t + 1);
        setToast({ message: 'Mapping updated', type: 'success' });
    };

    const handleManualMap = async (src: PickerItem, tgt: PickerItem) => {
        if (!locationId) return;
        const service = services.find(s => s.external_id === src.id);
        const calendar = calendars.find(c => c.external_id === tgt.id);
        if (!service || !calendar) throw new Error('Selected item not found');
        await serviceMappingApi.createManualMapping({
            gh_id: locationId,
            service_external_id: service.external_id,
            service_name: service.name,
            calendar_external_id: calendar.external_id,
            calendar_name: calendar.name,
        });
        setHistory(prev => [{ src: src.name, tgt: tgt.name }, ...prev]);
        setToast({ message: 'Mapping created', type: 'success' });
    };

    const srcItems: PickerItem[] = services.map(s => ({
        id: s.external_id,
        name: s.name,
        sub: s.description || (s.service_duration ? `${s.service_duration} ${s.service_duration_unit || 'min'}` : undefined),
    }));
    const tgtItems: PickerItem[] = calendars.map(c => ({ id: c.external_id, name: c.name, sub: c.description || c.calendar_type }));

    const aiGridCols = showConf ? '1.4fr_44px_1.5fr_150px_130px' : '1.4fr_44px_1.5fr_130px';

    return (
        <div className="mapper-ui font-sans text-mapper-text max-w-[1240px] mx-auto px-10 pt-[30px] pb-[70px]">
            {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
            <ConfirmDialog
                open={confirmApproveAll}
                title="Approve high-confidence matches?"
                message={`This approves ${highConfCount} match${highConfCount === 1 ? '' : 'es'} the AI is highly confident about (90% or higher) and connects them in Advital. You can still change any of them afterwards.`}
                confirmLabel="Approve all"
                onConfirm={() => { setConfirmApproveAll(false); handleApproveAll(); }}
                onCancel={() => setConfirmApproveAll(false)}
            />

            <div className="flex items-start justify-between gap-5 mb-[22px]">
                <div>
                    <h1 className="text-2xl font-extrabold tracking-[-0.5px] mb-1">Service Mapping</h1>
                    <p className="text-sm text-mapper-muted m-0 max-w-[560px]">
                        Map Advital services to the calendars patients book them through.
                    </p>
                </div>
                <SegmentedToggle
                    value={mode}
                    onChange={v => setMode(v as Mode)}
                    options={[
                        {
                            value: 'ai', label: 'AI Review', activeColorClass: 'text-mapper-teal-hover',
                            icon: (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /><circle cx="12" cy="12" r="3.2" />
                                </svg>
                            ),
                        },
                        {
                            value: 'manual', label: 'Manual', activeColorClass: 'text-mapper-purple',
                            icon: (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="18" r="2.2" /><path d="M8.2 6H14a4 4 0 0 1 4 4v5.6" />
                                </svg>
                            ),
                        },
                    ]}
                />
            </div>

            {!locationId ? (
                <div className="bg-mapper-white border border-mapper-border rounded-[15px] p-6 text-[13.5px] text-mapper-muted">
                    Select a location above to view its service mappings.
                </div>
            ) : mode === 'ai' ? (
                <div>
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <div className="relative flex-1 min-w-[220px] max-w-[340px]">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#98A2B0" strokeWidth="2" strokeLinecap="round" className="absolute left-3 top-1/2 -translate-y-1/2">
                                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
                            </svg>
                            <input
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Search unmapped items…"
                                className="w-full pl-[34px] pr-3 py-[9px] border border-mapper-border-2 rounded-[10px] text-[13.5px] bg-mapper-white outline-none text-mapper-text"
                            />
                        </div>
                        <div className="flex-1" />
                        <button
                            onClick={() => setShowConf(v => !v)}
                            title="Show or hide the confidence column"
                            className="flex items-center gap-[9px] py-2 pl-[14px] pr-3 rounded-[10px] border border-mapper-border-2 bg-mapper-white text-mapper-body-text text-[13px] font-semibold"
                        >
                            <span>Confidence</span>
                            <span className={`relative w-[34px] h-5 rounded-full transition-colors ${showConf ? 'bg-mapper-teal' : 'bg-mapper-border-2'}`}>
                                <span
                                    className="absolute top-[2px] w-4 h-4 rounded-full bg-white shadow-mapper-knob transition-all"
                                    style={{ left: showConf ? 16 : 2 }}
                                />
                            </span>
                        </button>
                        <button
                            onClick={requestApproveAll}
                            disabled={highConfCount === 0}
                            title={highConfCount === 0 ? 'No high-confidence matches waiting for review' : undefined}
                            className="flex items-center gap-[7px] px-[15px] py-[9px] rounded-[10px] border border-mapper-teal-border bg-mapper-teal-bg text-mapper-teal-hover text-[13px] font-semibold disabled:opacity-50 disabled:cursor-default"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            Approve all high-confidence
                        </button>
                        <button
                            onClick={handleRunAi}
                            disabled={triggering || isPolling}
                            className="flex items-center gap-[7px] px-[15px] py-[9px] rounded-[10px] bg-mapper-dark text-white text-[13px] font-semibold disabled:opacity-70"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /><circle cx="12" cy="12" r="3.2" />
                            </svg>
                            {triggering || isPolling ? 'Running…' : 'Run AI mapping'}
                        </button>
                    </div>

                    <div className="bg-mapper-white border border-mapper-border rounded-[15px] overflow-hidden">
                        <div
                            className="grid items-center gap-[14px] px-[22px] py-3 border-b border-mapper-border-3 bg-mapper-surface text-[11.5px] font-bold tracking-[0.4px] text-mapper-muted-3"
                            style={{ gridTemplateColumns: aiGridCols.replace(/_/g, ' ') }}
                        >
                            <div>ADVITAL SERVICE</div>
                            <div />
                            <div>ADVITAL CALENDAR</div>
                            {showConf && <div title="How sure the AI is that this is the right match (0–100%).">CONFIDENCE</div>}
                            <div className="text-right">STATUS / ACTIONS</div>
                        </div>

                        <div
                            className="grid items-center gap-[14px] px-[22px] py-[10px] border-b border-mapper-border-3 bg-mapper-white"
                            style={{ gridTemplateColumns: aiGridCols.replace(/_/g, ' ') }}
                        >
                            <div>
                                <Dropdown compact allLabel="All services" value={serviceNameFilter} options={filterOptions.service_name.map(v => ({ value: v, label: v }))} onChange={setServiceNameFilter} />
                            </div>
                            <div />
                            <div>
                                <Dropdown compact allLabel="All calendars" value={calendarNameFilter} options={filterOptions.calendar_name.map(v => ({ value: v, label: v }))} onChange={setCalendarNameFilter} />
                            </div>
                            {showConf && (
                                <div>
                                    <Dropdown compact allLabel="All confidence" value={confidenceTier} options={CONFIDENCE_TIER_OPTIONS} onChange={setConfidenceTier} />
                                </div>
                            )}
                            <div className="flex justify-end items-center gap-3">
                                {(statusFilter || confidenceTier || serviceNameFilter || calendarNameFilter || query) && (
                                    <button onClick={handleClearFilters} className="text-[12px] font-semibold text-mapper-teal whitespace-nowrap">
                                        Clear filters
                                    </button>
                                )}
                                <Dropdown compact allLabel="All statuses" value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />
                            </div>
                        </div>

                        {loadingSuggestions ? (
                            <div className="px-[22px] py-8 text-[13.5px] text-mapper-muted text-center">Loading…</div>
                        ) : suggestions.length === 0 ? (
                            <div className="px-[22px] py-8 text-[13.5px] text-mapper-muted text-center">No service mappings found.</div>
                        ) : (
                            suggestions.map(r => {
                                const actionable = r.status === 'pending';
                                return (
                                    <div
                                        key={r.id}
                                        className="grid items-center gap-[14px] px-[22px] py-[15px] border-b border-mapper-border-4 last:border-b-0"
                                        style={{ gridTemplateColumns: aiGridCols.replace(/_/g, ' ') }}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-[13.5px] font-bold truncate">{r.service_name}</div>
                                            <div className="text-[10.5px] text-mapper-muted-5 font-mono mt-[2px]">{r.service_external_id}</div>
                                        </div>
                                        <div className="flex justify-center text-mapper-muted-6">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14M13 6l6 6-6 6" />
                                            </svg>
                                        </div>
                                        <div className="min-w-0">
                                            {r.suggested_calendar_external_id ? (
                                                <>
                                                    <div className="text-[13.5px] font-semibold truncate">{r.suggested_calendar_name}</div>
                                                    <div className="text-[10.5px] text-mapper-muted-5 font-mono mt-[2px]">{r.suggested_calendar_external_id}</div>
                                                </>
                                            ) : (
                                                <span className="inline-flex items-center gap-[6px] text-[12.5px] font-semibold text-mapper-red bg-mapper-red-bg px-[10px] py-1 rounded-lg">
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                                                        <path d="M12 8v5M12 16v.5" /><circle cx="12" cy="12" r="9" />
                                                    </svg>
                                                    No match — map manually
                                                </span>
                                            )}
                                        </div>
                                        {showConf && (
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={() => setReasoningModal({ src: r.service_name, tgt: r.suggested_calendar_name || 'No match', reasoning: r.reasoning })}
                                                    title={r.reasoning || 'No explanation provided yet.'}
                                                    className="block w-full text-left cursor-pointer"
                                                >
                                                    <ConfidenceBar score={r.confidence_score} />
                                                </button>
                                            </div>
                                        )}
                                        <div className="flex flex-col items-end gap-[7px]">
                                            <StatusBadge status={r.status} />
                                            <ReviewedByCaption reviewedByName={r.reviewed_by_name} reviewedAt={r.reviewed_at} />
                                            {confirmDeleteId === r.id ? (
                                                <div className="flex items-center gap-[6px]">
                                                    <span className="text-[11.5px] font-semibold text-mapper-red">Delete?</span>
                                                    <button onClick={() => handleDelete(r.id)} disabled={deleting} className="px-2 py-1 rounded-md bg-mapper-red text-white text-[11px] font-semibold disabled:opacity-60">Yes</button>
                                                    <button onClick={() => setConfirmDeleteId(null)} disabled={deleting} className="px-2 py-1 rounded-md border border-mapper-border-2 text-mapper-muted text-[11px] font-semibold">No</button>
                                                </div>
                                            ) : (
                                                <div className="flex gap-[6px]">
                                                    {actionable && (
                                                        <>
                                                            <button onClick={() => handleApprove(r.id)} title="Approve" className="w-[30px] h-[30px] rounded-lg border border-mapper-teal-border bg-mapper-teal-bg text-mapper-teal-hover flex items-center justify-center">
                                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                            </button>
                                                            <button onClick={() => handleReject(r.id)} title="Reject" className="w-[30px] h-[30px] rounded-lg border border-mapper-red-border bg-mapper-red-bg-2 text-mapper-red flex items-center justify-center">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                                            </button>
                                                        </>
                                                    )}
                                                    <button onClick={() => openEdit(r)} title="Edit / remap" className="w-[30px] h-[30px] rounded-lg border border-mapper-border-2 bg-mapper-white text-mapper-muted flex items-center justify-center">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M11 4H4v16h16v-7" /><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" />
                                                        </svg>
                                                    </button>
                                                    <button onClick={() => setConfirmDeleteId(r.id)} title="Delete" className="w-[30px] h-[30px] rounded-lg border border-mapper-red-border bg-mapper-white text-mapper-red flex items-center justify-center">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M10 11v6M14 11v6" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        <Pagination page={page} totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))} onPageChange={setPage} />
                    </div>
                </div>
            ) : (
                <ManualPicker
                    srcLabel="ADVITAL SERVICE"
                    tgtLabel="ADVITAL CALENDAR"
                    srcItems={srcItems}
                    tgtItems={tgtItems}
                    loading={loadingManualData}
                    onMap={handleManualMap}
                    onError={(message) => setToast({ message, type: 'error' })}
                    history={history}
                />
            )}

            <ReasoningModal
                open={!!reasoningModal}
                onClose={() => setReasoningModal(null)}
                srcLabel={reasoningModal?.src ?? ''}
                tgtLabel={reasoningModal?.tgt ?? ''}
                reasoning={reasoningModal?.reasoning ?? ''}
            />

            <EditMappingModal
                open={!!editSuggestion}
                onClose={() => setEditSuggestion(null)}
                onMap={handleEditMap}
                onError={(message) => setToast({ message, type: 'error' })}
                tgtItems={editCalendars.map(c => ({ id: c.external_id, name: c.name, sub: c.description || c.calendar_type }))}
                tgtLabel="ADVITAL CALENDAR"
                srcLabel="SERVICE"
                loading={loadingEditCalendars}
                lockedSrc={editSuggestion ? { name: editSuggestion.service_name } : { name: '' }}
            />
        </div>
    );
};
